import { children, createEffect, createMemo, createSignal, createUniqueId, Show, Suspense } from 'solid-js';
import { Navigate } from '@solidjs/router';
import { Link, Meta, Title } from '@solidjs/meta';
import {
	Container,
	Heading,
	Picture,
	Section,
	Grid,
	GridThird,
	GridTwoThirds,
	ActivityIndicator,
	Button,
	Dialog,
} from '@troon/ui';
import { clientOnly } from '@solidjs/start';
import { createSearchStore } from '@troon/primitives/search-store';
import { useIsEnabled, useTrackEvent } from '@troon/analytics';
import { useUtmParams } from '@troon/primitives/utm';
import { getTeeTimeSearchSchema } from '@troon/tee-times/schema';
import { today } from '@internationalized/date';
import { useFacility } from '../../../../../providers/facility';
import { getSchemaString } from '../../../../../modules/schema/schema';
import { breadcrumbs } from '../../../../../modules/schema/breadcrumb-list';
import { image } from '../../../../../modules/schema/image';
import { webpage } from '../../../../../modules/schema/webpage';
import { TeeTimeSearchHeader } from '../../../../../components/tee-time-search-header';
import { clearFilters, TeeTimeFilters, TeeTimeFiltersFAB } from '../../../../../components/tee-time-search-filters';
import { ClosureMessage } from '../../../../../components/closure-message';
import { FacilityHeaderButtons } from '../../../../../components/facility/header-buttons';
import { FacilityBrand } from '../../../../../graphql';
import { useUser } from '../../../../../providers/user';
import { TeeTimeAlertCallout } from '../../../../../components/upsells/tee-time-alert-callout';
import { TeeTimeAlertButton } from '../../../../../components/tee-time-alert-button';
import { TeeTimeAlertForm } from '../../../../../partials/tee-time-alerts';
import { TeeTimeNoResultsAlert } from '../../../../../components/tee-time-no-results-alert';
import { SupportBadge } from '../../../../../components/support-badge';
import { UpsellAccessMinimal } from '../../../../../components/upsells/access-minimal';
import { createVisibilityReloader } from '../../../../../providers/visibility';
import { PayModeWarning } from '../../../../../components/tee-time-mode-warning';
import {
	teeTimeAdSeparation,
	TeeTimeInlineAdvertisement,
} from '../../../../../components/advertisement/tee-time-inline-advertisement';
import type { ComponentProps } from 'solid-js';
import type { RouteDefinition } from '@solidjs/router';

const TeeTimeResults = clientOnly(async () => ({
	default: (await import('@troon/tee-times/results')).TeeTimeResults,
}));

export default function ReserveTeeTime() {
	const facility = useFacility();
	const utm = useUtmParams();
	const user = useUser();
	const [filters, setFilters] = createSearchStore(getTeeTimeSearchSchema(facility().timezone));
	const trackEvent = useTrackEvent();
	const splitPaymentEnabled = useIsEnabled('points-plus-cash');
	const [payModeWarning, setPayModeWarning] = createSignal<ComponentProps<typeof PayModeWarning>['mode']>();

	createEffect(() => {
		if (
			filters.premiumPlusOffers &&
			((filters.payMode === 'points' && !facility().supportsPointsRedemption) ||
				(filters.payMode === 'split' && !facility().supportsSplitRedemption))
		) {
			setFilters({ payMode: 'dollars' });
			setPayModeWarning('points-premium');
		} else if (filters.payMode === 'points' && !facility().supportsPointsRedemption) {
			setFilters('payMode', 'dollars');
			setPayModeWarning('points');
		} else if (filters.payMode === 'split' && !facility().supportsSplitRedemption) {
			setFilters('payMode', 'dollars');
			setPayModeWarning('split');
		} else if (filters.premiumRates && !facility().supportsTroonAccess) {
			setFilters('premiumRates', false);
			setPayModeWarning('premium');
		}
	});
	createVisibilityReloader();

	const [teeTimeAlertRange, setTeeTimeAlertRange] = createSignal<null | [number, number]>(null);
	const teeTimeAlertDialogId = createUniqueId();
	const teeTimeAlertCourseSlugs = () =>
		filters.filterFacilities
			? Array.isArray(filters.filterFacilities)
				? filters.filterFacilities
				: [filters.filterFacilities]
			: undefined;

	createEffect(() => {
		// Manually triggered dialogs that don't use DialogTrigger must call dialogOpen
		if (teeTimeAlertRange()) {
			trackEvent('dialogOpened', { key: 'tee-time-alert-inline' });
		}
	});

	const respectDisplayConfig = createMemo(
		() =>
			!user()?.troonAccessProductType && utm().campaign === 'course-booking-link' && utm().source === facility().slug,
	);

	const teeTimes = children(() => (
		<Suspense fallback={<ActivityIndicator />}>
			<PayModeWarning mode={payModeWarning()} />
			<TeeTimeResults
				adRenderer={TeeTimeInlineAdvertisement}
				adSeparation={teeTimeAdSeparation}
				filterByCourse
				facilities={[facility()]}
				filters={filters}
				setFilters={setFilters}
				singleFacility
				teeTimeHref={(teeTime, params) => {
					const rateId = params.get('rateId');
					const taPlusRate = teeTime.rates.find((r) => rateId === r.id && r.rateType === 'TROON_ACCESS_PLUS');
					if ((!respectDisplayConfig() || facility().displayConfig.includes('RESERVE_ACCESS_RATE')) && taPlusRate) {
						params.set('rateId', taPlusRate.id);
						params.set('isPremium', '1');
					}

					const currentUtm = utm();
					const utmParams = {
						utm_campaign: currentUtm.campaign,
						utm_source: currentUtm.source,
						utm_medium: currentUtm.medium,
						utm_term: currentUtm.term,
						utm_content: currentUtm.content,
					};
					for (const [key, value] of Object.entries(utmParams)) {
						if (value) {
							params.set(key, value);
						}
					}

					return `/course/${teeTime.facility.slug}/reserve-tee-time/${teeTime.id}?${params.toString()}`;
				}}
				includeFeaturedOffers={
					!respectDisplayConfig()
						? facility().supportsTroonAccess
						: facility().displayConfig.includes('FEATURED_ACCESS_OFFERS')
				}
				includeInlineOffers={
					!respectDisplayConfig()
						? facility().supportsTroonAccess
						: facility().displayConfig.includes('INLINE_ACCESS_OFFERS')
				}
				includeUpsellBanner={
					!respectDisplayConfig()
						? facility().supportsTroonAccess
						: facility().displayConfig.includes('INLINE_ACCESS_UPSELL')
				}
				upsellBanner={
					(!respectDisplayConfig() && facility().supportsTroonAccess) ||
					facility().displayConfig.includes('INLINE_ACCESS_UPSELL') ? (
						<UpsellAccessMinimal location="single-course tee time search" />
					) : null
				}
				hideSplitOption={!splitPaymentEnabled}
				userAccessProductType={
					user()?.troonAccessProductType as ComponentProps<typeof TeeTimeResults>['userAccessProductType']
				}
				alertRenderer={(item) => (
					<TeeTimeAlertButton
						aria-haspopup="dialog"
						aria-controls={teeTimeAlertRange() ? teeTimeAlertDialogId : undefined}
						aria-expanded={!!teeTimeAlertRange()}
						onClick={() => setTeeTimeAlertRange([item().startAt, item().endAt])}
					>
						{item().content}
					</TeeTimeAlertButton>
				)}
				noResultsContent={() => <TeeTimeNoResultsAlert facilities={[facility()]} filters={filters} />}
			/>
			<Dialog
				id={teeTimeAlertDialogId}
				key="tee-time-alert-inline"
				open={!!teeTimeAlertRange()}
				onOpenChange={(change) => {
					if (!change.open) {
						setTeeTimeAlertRange(null);
					}
				}}
			>
				<TeeTimeAlertForm
					facilities={[facility()]}
					selectedDay={filters.date}
					selectedTime={teeTimeAlertRange()!}
					selectedPlayers={filters.players}
					courseSlugs={teeTimeAlertCourseSlugs()}
					onCancel={() => setTeeTimeAlertRange(null)}
					onSaved={() => setTeeTimeAlertRange(null)}
				/>
			</Dialog>
		</Suspense>
	));

	return (
		<Show when={facility().isBookable} fallback={<Navigate href={facility ? `/course/${facility().slug}` : ''} />}>
			<>
				<Title>{`${facility().name} | Book tee times | Golf with Access`}</Title>
				<Meta name="description" content={`Reserve golf tee times at ${facility().name} and earn Access Points.`} />
				<Link rel="canonical" href={`/course/${facility().slug}/reserve-tee-time?payMode=${filters.payMode}`} />
				<script
					type="application/ld+json"
					innerText={getSchemaString([
						breadcrumbs(`/course/${facility().slug}/reserve-tee-time`, [
							{ name: 'Home', pathname: '/' },
							{ name: 'Tee Times', pathname: '/tee-times' },
							{ name: facility().name ?? '', pathname: `/course/${facility().slug}` },
							{ name: 'Reserve tee times', pathname: `/course/${facility().slug}/reserve-tee-time` },
						]),
						facility().hero ? image(facility().hero!) : undefined,
						webpage(`/course/${facility().slug}`, {
							title: `${facility().name} | Reserve tee times | Golf with Access`,
						}),
					])}
				/>

				<div class="-mb-6 flex min-h-screen grow flex-col bg-neutral-100 md:-mb-12">
					<div class="bg-white">
						<Container class="flex-row flex-wrap items-center gap-3 pt-6 md:gap-8">
							<div class="order-1 flex aspect-square shrink-0 grow-0 basis-20 rounded-md border border-neutral bg-white p-2 md:basis-24 md:p-2">
								<Picture
									preload
									src={facility().logo}
									width={200}
									height={200}
									sizes="(min-width: 768px) 8rem, 4rem"
									mode="contain"
									class="w-full max-w-max rounded-lg object-cover"
								/>
							</div>
							<div class="order-3 flex shrink-0 grow basis-2/3 flex-col justify-around gap-2 sm:order-2 sm:basis-1/2">
								<div class="flex flex-col">
									<Heading as="h1" size="h4">
										{facility().name}
									</Heading>
									<p class="order-first text-sm tracking-widest text-neutral-800 uppercase">
										<Show when={facility().brand === FacilityBrand.TroonPartner}>Troon Partner Course</Show>
									</p>
									<Show when={facility().address}>
										{(address) => (
											<address
												class="text-sm text-neutral-800 not-italic"
												aria-label={`Address for ${facility().name}`}
											>
												{[
													address().street,
													address().city,
													[address().state, address().postalCode].filter(Boolean).join(' '),
												]
													.filter(Boolean)
													.join(', ')}
											</address>
										)}
									</Show>
								</div>
								<SupportBadge points={facility().supportsAccessPoints} rates={facility().supportsTroonAccess} />
							</div>
							<div class="order-2 flex grow justify-end gap-1 text-neutral-800 sm:order-3">
								<FacilityHeaderButtons
									facility={facility()}
									teeTimeAlertDefaults={{
										selectedDay: filters.date,
										selectedTime: [filters.startAt, filters.endAt],
										selectedPlayers: filters.players,
										courseSlugs: teeTimeAlertCourseSlugs(),
									}}
								/>
							</div>
						</Container>
					</div>

					<TeeTimeSearchHeader
						class="border-b border-neutral"
						trackQuery={false}
						maxDate={today(facility().timezone).add({
							days: Math.max(...(facility()?.courses.flatMap((course) => course.bookingWindowInDays) ?? [14])),
						})}
						timezone={facility()?.timezone}
						hideQueryInput
						filters={filters}
						setFilters={setFilters}
						scrollHeader={
							<div class="@container flex gap-2">
								<div class="hidden aspect-square shrink-0 grow-0 basis-16 rounded-md border border-neutral bg-white p-1 @xs:flex">
									<Picture
										preload
										src={facility().logo}
										width={200}
										height={200}
										sizes="(min-width: 768px) 8rem, 4rem"
										mode="contain"
										class="w-full max-w-max rounded-lg object-cover"
									/>
								</div>
								<div class="order-3 flex shrink-0 grow basis-2/3 flex-col justify-around gap-2 sm:order-2 sm:basis-1/2">
									<div class="flex flex-col">
										<Heading as="h1" size="h4">
											{facility().name}
										</Heading>
										<p class="order-first text-sm tracking-widest text-neutral-800 uppercase">
											<Show when={facility().brand === FacilityBrand.TroonPartner}>Troon Partner Course</Show>
										</p>
										<Show when={facility().address}>
											{(address) => (
												<address
													class="text-sm text-neutral-800 not-italic"
													aria-label={`Address for ${facility().name}`}
												>
													{[
														address().street,
														address().city,
														[address().state, address().postalCode].filter(Boolean).join(' '),
													]
														.filter(Boolean)
														.join(', ')}
												</address>
											)}
										</Show>
									</div>
									<SupportBadge
										points={facility().supportsAccessPoints}
										rates={facility().supportsTroonAccess}
										class="text-sm font-semibold"
									/>
								</div>
							</div>
						}
					/>

					<Container class="justify-self-stretch">
						<Section class="pt-4 lg:pt-8">
							<ClosureMessage
								notes={facility().notes}
								closures={facility().closures}
								date={filters.date}
								timezone={facility().timezone}
							/>
							<Show
								when={
									(respectDisplayConfig()
										? facility().displayConfig.includes('ACCESS_FILTER') && facility().supportsTroonAccess
										: facility().supportsTroonAccess) || facility().courses?.length > 1
								}
								fallback={teeTimes()}
							>
								<Grid class="grid-rows-2 gap-y-0 md:grid-rows-1">
									<GridThird class="order-2 md:order-1">
										<div class="top-54 flex-col gap-y-8 pb-8 md:sticky lg:flex xl:top-34">
											<div
												// Manual top calculations are depend on the height of the TeeTimeSearchFields section
												class="hidden max-h-[calc(100dvh-15rem)] flex-col gap-6 overflow-auto rounded-lg border border-neutral bg-white p-6 md:flex xl:max-h-[calc(100dvh-11rem)]"
											>
												<div class="flex items-center justify-between">
													<Heading as="h2" size="h5">
														Filter Results
													</Heading>
													<Button
														type="reset"
														size="sm"
														appearance="transparent-current"
														class="-me-3 size-fit grow-0 font-medium text-neutral-700 normal-case"
														onClick={() => {
															clearFilters(setFilters);
														}}
													>
														Clear All
													</Button>
												</div>

												<TeeTimeFilters
													courses={facility().courses}
													filters={filters}
													setFilters={setFilters}
													supportsPoints={facility().supportsAccessPoints}
													supportsPremium={facility().supportsTroonAccess && !respectDisplayConfig()}
												/>
											</div>
											<Show when={facility().courses.some((c) => c.supportsTeeTimeAlerts)}>
												<TeeTimeAlertCallout
													facilities={[facility()]}
													selectedDay={filters.date}
													selectedTime={[filters.startAt, filters.endAt]}
													selectedPlayers={filters.players}
													courseSlugs={teeTimeAlertCourseSlugs()}
												/>
											</Show>
										</div>
									</GridThird>
									<GridTwoThirds class="order-1 flex min-h-[calc(100dvh-15rem)] flex-col gap-8 md:order-2">
										{teeTimes()}
									</GridTwoThirds>
								</Grid>
								<div class="lg:hidden">
									<TeeTimeFiltersFAB
										courses={facility().courses}
										filters={filters}
										setFilters={setFilters}
										supportsPoints={facility().supportsAccessPoints}
										supportsPremium={facility().supportsTroonAccess && !respectDisplayConfig()}
									/>
								</div>
							</Show>
						</Section>
					</Container>
				</div>
			</>
		</Show>
	);
}

export const route = { info: { nav: { sticky: false } } } satisfies RouteDefinition;
